import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  LinearProgress,
  Collapse,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  CheckCircle,
  RadioButtonUnchecked,
  ChevronRight,
  ExpandMore,
  Close,
  Checklist,
} from "@mui/icons-material";
import {
  useSetupChecklist,
  ChecklistItem,
} from "../../hooks/useSetupChecklist";
import { useOrganization } from "../../context/OrganizationContext";
import OrganizationSettingsDialog from "../layout/OrganizationSettingsDialog";
import TeamManagement from "../settings/TeamManagement";

export default function SetupChecklist() {
  const {
    items,
    completedCount,
    totalCount,
    progressPercent,
    isVisible,
    dismiss,
  } = useSetupChecklist();

  const { organization, updateOrganization } = useOrganization();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    // Mark settings as reviewed when the dialog is closed, even without saving.
    // The owner saw the defaults — that's enough to complete this step.
    if (organization && !organization.settings?.settingsConfigured) {
      updateOrganization({
        settings: { ...organization.settings, settingsConfigured: true },
      });
    }
  };

  if (!isVisible) return null;

  const handleItemClick = (item: ChecklistItem) => {
    if (item.action === "openSettings") {
      setSettingsOpen(true);
    } else if (item.action === "openTeamManagement") {
      setTeamDialogOpen(true);
    } else if (item.route) {
      navigate(item.route);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1050,
          maxWidth: { xs: "calc(100% - 32px)", sm: 360 },
          width: { xs: "calc(100% - 32px)", sm: 360 },
        }}
      >
        <Collapse in={!expanded}>
          {/* Minimized pill */}
          <Paper
            elevation={6}
            onClick={() => setExpanded(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1.25,
              borderRadius: 50,
              cursor: "pointer",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              ml: "auto",
              width: "fit-content",
              "&:hover": {
                bgcolor: "primary.dark",
              },
              transition: "background-color 0.2s",
            }}
          >
            <Checklist fontSize="small" />
            <Typography variant="body2" fontWeight={600}>
              {completedCount}/{totalCount} Setup
            </Typography>
          </Paper>
        </Collapse>

        <Collapse in={expanded}>
          {/* Expanded card */}
          <Paper
            elevation={8}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                pt: 2,
                pb: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Setup Checklist
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {completedCount} of {totalCount} completed
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton size="small" onClick={() => setExpanded(false)}>
                  <ExpandMore fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Progress bar */}
            <Box sx={{ px: 2.5, pb: 1.5 }}>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "action.hover",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            {/* Checklist items */}
            <Box
              sx={{
                overflowY: "auto",
                px: 1,
                pb: 1,
              }}
            >
              {items.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                    transition: "background-color 0.15s",
                  }}
                >
                  {/* Checkbox icon */}
                  {item.completed ? (
                    <CheckCircle
                      color="success"
                      sx={{ fontSize: 22, flexShrink: 0 }}
                    />
                  ) : (
                    <RadioButtonUnchecked
                      sx={{
                        fontSize: 22,
                        color: "text.disabled",
                        flexShrink: 0,
                      }}
                    />
                  )}

                  {/* Label and description */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={item.completed ? 400 : 600}
                        sx={{
                          textDecoration: item.completed
                            ? "line-through"
                            : "none",
                          color: item.completed
                            ? "text.secondary"
                            : "text.primary",
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.optional && (
                        <Chip
                          label="Optional"
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        lineHeight: 1.3,
                        mt: 0.25,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>

                  {/* Chevron */}
                  <ChevronRight
                    sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0 }}
                  />
                </Box>
              ))}
            </Box>

            {/* Dismiss link */}
            <Box sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
              <Button
                size="small"
                color="inherit"
                onClick={dismiss}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  p: 0,
                  minWidth: "auto",
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Do Not Show Again
              </Button>
            </Box>
          </Paper>
        </Collapse>
      </Box>

      {/* Settings dialog */}
      <OrganizationSettingsDialog
        open={settingsOpen}
        onClose={handleSettingsClose}
      />

      {/* Team management dialog */}
      <Dialog
        open={teamDialogOpen}
        onClose={() => setTeamDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Team Management
          </Typography>
          <IconButton onClick={() => setTeamDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TeamManagement />
        </DialogContent>
      </Dialog>
    </>
  );
}
